/**
 * AttestModal — collects the expert's statement before the WebAuthn ceremony.
 *
 * Simple modal overlay: text area for the statement, Cancel, and Confirm.
 * Confirm triggers the WebAuthn assertion flow (caller-supplied onConfirm).
 * The modal is online-only; the ProvenancePanel / VehicleDetail gate it.
 *
 * Accessibility:
 *   - role="dialog", aria-modal="true", aria-labelledby.
 *   - Focus is placed on the first field via useEffect + ref.
 *   - Escape key closes the modal (window event listener, no jsx-a11y conflict).
 *   - 44px min-height on all interactive elements.
 */

import { Button } from '@/components/Button';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { useModalFocus } from '@/hooks/useModalFocus';
import type { CSSProperties } from 'react';
import { useEffect, useId } from 'react';

export interface AttestModalProps {
  isOpen: boolean;
  /** Error message from the mutation, if any. */
  error: string | null;
  /** Whether the attestation mutation is in flight. */
  isPending: boolean;
  /** Pre-fills the textarea on open (uncontrolled). Used to restore a
   * stashed statement after the register-first-then-attest detour, so the
   * user doesn't have to re-type. */
  defaultStatement?: string | undefined;
  onConfirm: (statement: string) => void;
  onCancel: () => void;
}

export function AttestModal({
  isOpen,
  error,
  isPending,
  defaultStatement,
  onConfirm,
  onCancel,
}: AttestModalProps) {
  const titleId = useId();
  const { dialogRef, initialRef: textareaRef } = useModalFocus<HTMLDivElement, HTMLTextAreaElement>(
    isOpen,
  );

  // Escape key closes the modal (not while pending — cancel is disabled).
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isPending, onCancel]);

  if (!isOpen) return null;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const statement = (data.get('statement') as string | null)?.trim() ?? '';
    if (!statement) return;
    onConfirm(statement);
  }

  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACE.md,
    zIndex: 1000,
  };

  const dialogStyle: CSSProperties = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    padding: `${SPACE.md}px`,
    maxWidth: 480,
    width: '100%',
    fontFamily: F.body,
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: T.text,
    marginBottom: SPACE.sm,
  };

  const textareaStyle: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 100,
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    padding: SPACE.sm,
    color: T.text,
    fontSize: 13,
    fontFamily: F.body,
    lineHeight: 1.6,
    resize: 'vertical',
  };

  return (
    <div style={overlayStyle}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={dialogStyle}
      >
        <h2
          id={titleId}
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: T.text,
            margin: 0,
            marginBottom: SPACE.md,
          }}
        >
          Attest this vehicle
        </h2>

        <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: SPACE.md }}>
          Your statement will be cryptographically bound to the current ledger head via your
          registered passkey. This cannot be undone.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="attest-statement">
            Statement
          </label>
          <textarea
            ref={textareaRef}
            id="attest-statement"
            name="statement"
            style={textareaStyle}
            placeholder="e.g. Chassis number matches records. No flood damage. Original engine."
            required
            minLength={10}
            maxLength={500}
            disabled={isPending}
            defaultValue={defaultStatement ?? ''}
          />
          <p style={{ fontSize: 11, color: T.textMuted, marginTop: SPACE.xs, lineHeight: 1.5 }}>
            10–500 characters. Keep it factual.
          </p>

          {error ? (
            <p
              role="alert"
              style={{
                fontSize: 13,
                color: T.red,
                marginTop: SPACE.sm,
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: SPACE.sm,
              marginTop: SPACE.md,
              justifyContent: 'flex-end',
            }}
          >
            <Button variant="secondary" size="md" disabled={isPending} onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" size="md" disabled={isPending} type="submit">
              {isPending ? 'Waiting for passkey…' : 'Confirm with passkey'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
